import { Search } from 'lucide-react'
import { useDeferredValue, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { MetricStrip } from '../components/MetricStrip'
import { Pagination } from '../components/Pagination'
import { TrendChart } from '../components/TrendChart'
import { mergePinduoduoProducts, usePinduoduoResource, type PinduoduoCatalogProduct } from '../platform'
import { integer, money } from '../utils'
import { pinduoduoQueryWindowLabel } from '../pinduoduo'

type SourceFilter = 'all' | 'promotion' | 'opportunity' | 'both'
type ProductSortKey = 'spend' | 'netGmv' | 'netOrders' | 'sales30d' | 'clicks' | 'name'
type PromotionTrendKey = 'spend' | 'attributedGmv' | 'netGmv' | 'settledGmv'

const promotionTrendOptions: Array<{ key: PromotionTrendKey; label: string }> = [
  { key: 'spend', label: '推广花费' },
  { key: 'attributedGmv', label: '归因交易额' },
  { key: 'netGmv', label: '净交易额' },
  { key: 'settledGmv', label: '结算交易额' },
]

const reportedMoney = (value: number | null) => value === null ? '—' : money(value)
const reportedInteger = (value: number | null) => value === null ? '—' : integer(value)
const reportedRatio = (value: number | null) => value === null ? '—' : value.toFixed(2)

const sourceLabel = (product: PinduoduoCatalogProduct) => product.sourceCount === 2
  ? '推广 + 活动机会'
  : product.promotion
    ? '推广商品'
    : '活动机会'

const matchesSource = (product: PinduoduoCatalogProduct, source: SourceFilter) => {
  if (source === 'promotion') return Boolean(product.promotion)
  if (source === 'opportunity') return Boolean(product.opportunity)
  if (source === 'both') return product.sourceCount === 2
  return true
}

const sortValue = (product: PinduoduoCatalogProduct, key: ProductSortKey): string | number => {
  if (key === 'name') return product.name
  if (key === 'sales30d') return product.opportunity?.sales30d ?? -1
  return product.promotion?.[key] ?? -1
}

export function PinduoduoProductsPage() {
  const { data, error, loading, reload } = usePinduoduoResource()
  const [search, setSearch] = useState('')
  const deferredSearch = useDeferredValue(search)
  const [source, setSource] = useState<SourceFilter>('all')
  const [sortKey, setSortKey] = useState<ProductSortKey>('spend')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [promotionTrendKey, setPromotionTrendKey] = useState<PromotionTrendKey>('spend')

  const products = useMemo(() => data ? mergePinduoduoProducts(data) : [], [data])
  const overlapCount = useMemo(() => products.filter((product) => product.sourceCount === 2).length, [products])
  const promotionTrendData = useMemo(() => (data?.daily || []).flatMap((item) => {
    const value = item[promotionTrendKey]
    return value == null ? [] : [{ date: item.date, value }]
  }), [data, promotionTrendKey])
  const filteredProducts = useMemo(() => {
    const term = deferredSearch.trim().toLocaleLowerCase('zh-CN')
    return products
      .filter((product) => matchesSource(product, source))
      .filter((product) => {
        if (!term) return true
        const searchable = [
          product.name,
          product.productId,
          product.promotion?.promotionName,
          product.promotion?.group,
          product.opportunity?.benefit,
        ].filter(Boolean).join(' ').toLocaleLowerCase('zh-CN')
        return searchable.includes(term)
      })
      .sort((left, right) => {
        const leftValue = sortValue(left, sortKey)
        const rightValue = sortValue(right, sortKey)
        if (typeof leftValue === 'string' && typeof rightValue === 'string') return leftValue.localeCompare(rightValue, 'zh-CN')
        return Number(rightValue) - Number(leftValue)
      })
  }, [deferredSearch, products, sortKey, source])

  const pageCount = Math.max(1, Math.ceil(filteredProducts.length / pageSize))
  const currentPage = Math.min(page, pageCount)
  const visibleProducts = filteredProducts.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  if (loading) return <div className="loading-state">正在加载拼多多商品数据…</div>
  if (error || !data) return <div className="error-state"><h1>拼多多商品数据加载失败</h1><p>{error}</p><button type="button" className="primary-button" onClick={() => void reload()}>重试</button></div>
  const queryWindowLabel = pinduoduoQueryWindowLabel(data.meta.queryStart, data.meta.queryEnd)
  const activeDays = data.daily.filter((item) => item.spend != null && item.spend > 0).length
  const clickRate = data.summary.impressions && data.summary.clicks != null
    ? data.summary.clicks / data.summary.impressions * 100
    : null
  const promotionTrendOption = promotionTrendOptions.find((item) => item.key === promotionTrendKey)!

  return (
    <div className="page-stack pdd-page">
      <div className="page-title-row">
        <div>
          <span className="pdd-platform-label">PINDUODUO</span>
          <h1>拼多多商品</h1>
          <p>{data.meta.storeName} · 推广商品与活动机会商品按平台商品ID去重</p>
        </div>
        <div className="page-title-actions">
          <DataFreshnessBadge date={data.meta.queryEnd} label="推广数据截至" mode="on-demand" />
          <Link className="text-link" to="/pinduoduo">返回拼多多总览</Link>
        </div>
      </div>

      <div className="pdd-caliber-note">
        <strong>商品粒度：</strong>
        当前是拼多多平台商品ID粒度，不是京东SPU/SKU。尚未取得稳定的官方规格ID，因此不展示或推测拼多多SKU。
      </div>

      <MetricStrip items={[
        { label: '去重商品', value: integer(products.length), context: '以平台商品ID合并' },
        { label: '推广商品', value: integer(data.products.length), context: `${data.meta.queryStart} 至 ${data.meta.queryEnd}` },
        { label: '活动机会商品', value: integer(data.marketingOpportunities.length), context: '30日销量与活动价口径' },
        { label: '两类来源重合', value: integer(overlapCount), context: '同一商品不重复计数' },
      ]} />

      <section className="overview-section pdd-trend-section">
        <div className="section-heading-row">
          <div><h2>{queryWindowLabel}推广趋势</h2><p>{data.meta.queryStart} 至 {data.meta.queryEnd} · 包含无花费日期，停投状态不会被隐藏</p></div>
          <div className="metric-switch" role="group" aria-label="拼多多推广趋势指标">
            {promotionTrendOptions.map((option) => <button type="button" key={option.key} className={promotionTrendKey === option.key ? 'is-active' : ''} aria-pressed={promotionTrendKey === option.key} onClick={() => setPromotionTrendKey(option.key)}>{option.label}</button>)}
          </div>
        </div>
        <TrendChart data={promotionTrendData} valueFormatter={money} valueLabel={promotionTrendOption.label} ariaLabel={`拼多多${queryWindowLabel}${promotionTrendOption.label}趋势`} />
        <div className="pdd-trend-footnote">{activeDays}天有花费 · 曝光 {reportedInteger(data.summary.impressions)} · 点击 {reportedInteger(data.summary.clicks)} · 点击率 {clickRate == null ? '—' : `${clickRate.toFixed(2)}%`} · 最后有花费 {data.meta.promotionLastActiveDate || '未知日期'}</div>
      </section>

      <section className="overview-section">
        <div className="section-heading-row"><div><h2>商品筛选</h2><p>缺失来源显示为“—”，不视为零值</p></div><span>命中 {integer(filteredProducts.length)} 个</span></div>
        <div className="filter-bar">
          <label className="search-control">
            <Search aria-hidden="true" />
            <span className="sr-only">搜索拼多多商品</span>
            <input value={search} placeholder="搜索商品名、商品ID、推广计划" onChange={(event) => { setSearch(event.target.value); setPage(1) }} />
          </label>
          <label><span className="sr-only">数据来源</span><select value={source} onChange={(event) => { setSource(event.target.value as SourceFilter); setPage(1) }}>
            <option value="all">全部来源</option><option value="promotion">有推广数据</option><option value="opportunity">有活动机会</option><option value="both">两类来源都有</option>
          </select></label>
          <label><span className="sr-only">排序方式</span><select value={sortKey} onChange={(event) => { setSortKey(event.target.value as ProductSortKey); setPage(1) }}>
            <option value="spend">按推广花费降序</option><option value="netGmv">按净交易额降序</option><option value="netOrders">按净成交笔数降序</option><option value="sales30d">按30日销量降序</option><option value="clicks">按点击降序</option><option value="name">按商品名排序</option>
          </select></label>
        </div>
      </section>

      <section className="overview-section">
        <div className="section-heading-row"><div><h2>商品明细</h2><p>推广指标为{queryWindowLabel}汇总；活动机会销量为30日口径</p></div></div>
        {visibleProducts.length === 0 ? <div className="empty-state">没有符合当前条件的拼多多商品。</div> : <>
          <div className="table-responsive">
            <table className="pdd-table">
              <thead><tr><th>商品</th><th>数据来源</th><th>推广花费</th><th>归因交易额</th><th>净交易额</th><th>净ROI</th><th>净成交</th><th>30日销量</th><th>当前价 / 活动价</th></tr></thead>
              <tbody>{visibleProducts.map((product) => <tr key={product.productId}>
                <td><Link className="text-link" to={`/pinduoduo/product/${encodeURIComponent(product.productId)}`}><strong>{product.name}</strong></Link><small>商品ID {product.productId}{product.promotion?.group ? ` · ${product.promotion.group}` : ''}</small></td>
                <td><span className={`inventory-status ${product.sourceCount === 2 ? 'is-normal' : 'is-stagnant'}`}>{sourceLabel(product)}</span></td>
                <td>{product.promotion ? reportedMoney(product.promotion.spend) : '—'}</td>
                <td>{product.promotion ? reportedMoney(product.promotion.attributedGmv) : '—'}</td>
                <td>{product.promotion ? reportedMoney(product.promotion.netGmv) : '—'}</td>
                <td>{product.promotion ? reportedRatio(product.promotion.netRoi) : '—'}</td>
                <td>{product.promotion ? reportedInteger(product.promotion.netOrders) : '—'}</td>
                <td>{product.opportunity ? integer(product.opportunity.sales30d) : '—'}</td>
                <td>{product.opportunity ? `¥${product.opportunity.currentPrice} / ¥${product.opportunity.campaignPrice}` : '—'}</td>
              </tr>)}</tbody>
            </table>
          </div>
          <Pagination page={currentPage} pageCount={pageCount} total={filteredProducts.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1) }} />
        </>}
      </section>
    </div>
  )
}
